import {
  usePartnerShortQuery,
  useOperatorShortQuery,
  useBuildingsCsvDownloadQuery,
} from '@common/cache';
import { paths } from '@common/types';
import { debounce, upperCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useBuildingTableStore,
  PersistedBuildingColumn,
} from '../Filters/filters.store';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';

export type IBuildingTableEntry =
  paths['/record/buildings']['get']['responses']['200']['content']['application/json']['records'][number];

export type ColumnNames = keyof IBuildingTableEntry;

const buildBuildingColumns = (
  isDense: boolean,
): GridColDef<IBuildingTableEntry>[] => [
  {
    field: 'name',
    headerName: 'Building',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.name}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'partner_name',
    headerName: 'Partner',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.partner_name}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'operator_name',
    headerName: 'Operator',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.operator_name}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'wellness_coordinators',
    headerName: 'Wellness Coordinator',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.wellness_coordinators}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.city}
          variant='body2'
          width='100%'
        />
        {!isDense && (
          <TruncatedTextWithConditionalTooltip
            variant='caption'
            color='textSecondary'
            text={params.row.state}
            width='100%'
          />
        )}
      </Box>
    ),
  },
  {
    field: 'address',
    headerName: 'Address',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.address}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'total_beds',
    headerName: 'Total Beds',
    width: 120,
    minWidth: 120,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.total_beds}
        variant='body2'
        width='100%'
      />
    ),
  },
];

export const useBuildingTable = () => {
  const {
    filters,
    setFilters,
    isFiltersCardOpen,
    clearStore,
    syncDraftWithAppliedFilters,
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useBuildingTableStore();
  const { sort_by, sort_order } = filters;

  const apiRef = useGridApiRef();

  const { data: partnerShortOptions = [] } = usePartnerShortQuery();
  const { data: operatorShortOptions = [] } = useOperatorShortQuery();

  const { data, isPending, mutateAsync } = useBuildingsCsvDownloadQuery();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `buildings`;
      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }
      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  useEffect(() => {
    return () => {
      syncDraftWithAppliedFilters();
    };
  }, []);

  const handleDownload = async () => {
    const loadingToast = toast.loading('Downloading Building Table...', {
      isLoading: true,
      autoClose: 500,
    });
    try {
      await mutateAsync({
        sort_by: filters.sort_by,
        sort_order: upperCase(filters.sort_order) as 'ASC' | 'DESC',
        ...(filters.search !== '' && { search: filters.search }),
        ...(filters.partner_id !== '' && { partner_id: filters.partner_id }),
        ...(filters.operator_id !== '' && { operator_id: filters.operator_id }),
        ...(filters.state !== '' && { state: filters.state }),
        ...(filters.county !== '' && { county: filters.county }),
      });
      toast.success('Downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ ...filters, sort_by: 'name', sort_order: 'asc' });
      return;
    }
    setFilters({
      ...filters,
      sort_by: field as typeof sort_by,
      sort_order: sort,
    });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ ...filters, limit: model.pageSize, page: model.page + 1 });
  };

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters({ ...filters, search: value });
      }, 500),
    [filters, setFilters],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInputValue(value);
    debouncedSetSearch(value);
  };

  const runtimeColumns = useMemo(
    () => buildBuildingColumns(density === 'dense'),
    [density],
  );

  const columns = buildGridColumns<
    IBuildingTableEntry,
    PersistedBuildingColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<IBuildingTableEntry>,
  ): PersistedBuildingColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );

    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    const nextHidden = columns
      .map(col => col.field)
      .filter(field => model[field] === false);
    setHiddenColumns(nextHidden);
  };

  const MIN_VISIBLE_COLUMNS = 4;
  const visibleColumnsCount = Object.values(columnVisibilityModel).filter(
    Boolean,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;
  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  const handleResetAll = () => {
    clearStore();
    setSearchInputValue('');
    resetView();
  };

  return {
    filters,
    isFiltersCardOpen,
    columns: columnsWithHideable,
    handleChangeOrder,
    onPageModelChange,
    handleSearchChange,
    sort_by,
    sort_order,
    partnerShortOptions: partnerShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    operatorShortOptions: operatorShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    handleDownload,
    isDownloading: isPending,
    searchInputValue,
    setSearchInputValue,
    handleResetAll,
    initialSortFilters: {
      sort_order: 'asc',
      sort_by: 'name',
    },
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    density,
    handleDensityChange: setDensity,
    apiRef,
  };
};
